import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  getRetreatPlans, createRetreatPlan, updateRetreatPlan, deleteRetreatPlan,
  RETREAT_PLAN_STATUSES, fmtDate,
} from "@/lib/api";

// --- Grid sizing (compact, full year fits without vertical scroll) ---
const ROW_H = 22;                  // px per day row
const HEADER_H = 44;               // month header row
const DAY_COL_W = 32;              // left-side day-number column
const MIN_MONTH_COL_W = 96;        // each month column min width
const OPERATING_MONTHS = new Set([3, 4, 5, 6, 7, 8, 9, 10, 11]); // Mar–Nov
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// --- Date helpers (local, no UTC drift) ---
const isoDay = (y, m, d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const parseIso = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (iso, n) => {
  const d = parseIso(iso);
  d.setDate(d.getDate() + n);
  return isoDay(d.getFullYear(), d.getMonth() + 1, d.getDate());
};
const daysInMonth = (y, m) => new Date(y, m, 0).getDate();
const todayIso = () => {
  const d = new Date();
  return isoDay(d.getFullYear(), d.getMonth() + 1, d.getDate());
};

const statusByValue = (v) => RETREAT_PLAN_STATUSES.find(s => s.value === v) || RETREAT_PLAN_STATUSES[2];

// Build one segment per (plan, month) intersection so a retreat that spans
// Mar 28 → Apr 4 renders as two visual blocks (Mar 28–31 and Apr 1–4).
const segmentsForMonth = (plans, year, month) => {
  const dim = daysInMonth(year, month);
  const segs = [];
  for (const p of plans) {
    const ps = parseIso(p.start_date);
    const pe = parseIso(p.end_date);
    if (Number.isNaN(ps.getTime()) || Number.isNaN(pe.getTime())) continue;
    const ms = new Date(year, month - 1, 1);
    const me = new Date(year, month - 1, dim);
    if (pe < ms || ps > me) continue;
    const startDay = ps < ms ? 1 : ps.getDate();
    const endDay = pe > me ? dim : pe.getDate();
    segs.push({ plan: p, startDay, endDay });
  }
  // Pack into vertical tracks (sub-columns) within this month when overlapping.
  const sorted = [...segs].sort((a, b) => a.startDay - b.startDay || a.plan.start_date.localeCompare(b.plan.start_date));
  const tracks = []; // tracks[i] = list of segs with lastEndDay
  const placement = new Map(); // seg -> trackIdx
  for (const s of sorted) {
    let placed = -1;
    for (let i = 0; i < tracks.length; i++) {
      const last = tracks[i][tracks[i].length - 1];
      if (last.endDay < s.startDay) { tracks[i].push(s); placed = i; break; }
    }
    if (placed === -1) { tracks.push([s]); placed = tracks.length - 1; }
    placement.set(s, placed);
  }
  return { segments: sorted, trackCount: Math.max(1, tracks.length), placement };
};

export default function AnnualCalendar() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { setPlans(await getRetreatPlans(year)); }
    catch { toast.error("Failed to load planning calendar"); }
  };
  useEffect(() => { load(); }, [year]);

  const yearsToOffer = useMemo(() => {
    const ys = new Set([currentYear - 1, currentYear, currentYear + 1, currentYear + 2]);
    plans.forEach(p => p.year && ys.add(p.year));
    return [...ys].sort();
  }, [plans, currentYear]);

  // Pre-compute month columns once per (plans, year) change.
  const monthCols = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const dim = daysInMonth(year, month);
      const { segments, trackCount, placement } = segmentsForMonth(plans, year, month);
      return { month, dim, segments, trackCount, placement, operating: OPERATING_MONTHS.has(month) };
    });
  }, [plans, year]);

  // --- Drag / resize (vertical) ---
  const dragRef = useRef(null);
  const onBarMouseDown = (mode, plan) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    const startY = e.clientY;
    const origStart = plan.start_date;
    const origEnd = plan.end_date;
    dragRef.current = { mode, plan, startY, origStart, origEnd, deltaDays: 0, moved: false };

    const onMove = (ev) => {
      const dy = ev.clientY - startY;
      const delta = Math.round(dy / ROW_H);
      if (delta === dragRef.current.deltaDays) return;
      dragRef.current.deltaDays = delta;
      dragRef.current.moved = true;
      // Live ghost update (in-state only).
      setPlans(prev => prev.map(p => {
        if (p.id !== plan.id) return p;
        if (mode === "move") {
          return { ...p, start_date: addDays(origStart, delta), end_date: addDays(origEnd, delta) };
        }
        if (mode === "resize-top") {
          const ns = addDays(origStart, delta);
          if (ns > origEnd) return p;
          return { ...p, start_date: ns };
        }
        if (mode === "resize-bottom") {
          const ne = addDays(origEnd, delta);
          if (ne < origStart) return p;
          return { ...p, end_date: ne };
        }
        return p;
      }));
    };

    const onUp = async () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const d = dragRef.current;
      dragRef.current = null;
      if (!d?.moved || d.deltaDays === 0) return;
      const patch = (mode === "move")
        ? { start_date: addDays(d.origStart, d.deltaDays), end_date: addDays(d.origEnd, d.deltaDays) }
        : (mode === "resize-top")
          ? { start_date: addDays(d.origStart, d.deltaDays) }
          : { end_date: addDays(d.origEnd, d.deltaDays) };
      try {
        await updateRetreatPlan(d.plan.id, patch);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't save");
      } finally {
        load();
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // --- Click empty cell to create ---
  const onCellClick = (month, day) => {
    const dim = daysInMonth(year, month);
    const start = isoDay(year, month, day);
    const endDay = Math.min(dim, day + 2); // default 3-day block
    const end = isoDay(year, month, endDay);
    setEditing({ mode: "create", name: "", start_date: start, end_date: end, status: "interested", notes: "" });
  };

  // --- Dialog save / delete ---
  const saveFromDialog = async () => {
    const data = editing;
    if (!data?.name?.trim()) return toast.error("Name required");
    if (!data.start_date || !data.end_date) return toast.error("Dates required");
    if (data.end_date < data.start_date) return toast.error("End must be on or after start");
    try {
      if (data.id) {
        await updateRetreatPlan(data.id, {
          name: data.name.trim(),
          start_date: data.start_date,
          end_date: data.end_date,
          status: data.status,
          notes: data.notes || "",
        });
        toast.success("Saved");
      } else {
        await createRetreatPlan({
          name: data.name.trim(),
          start_date: data.start_date,
          end_date: data.end_date,
          status: data.status,
          notes: data.notes || "",
        });
        toast.success("Block added");
        const newYear = parseInt(String(data.start_date).slice(0, 4), 10);
        if (!Number.isNaN(newYear) && newYear !== year) setYear(newYear);
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async () => {
    if (!editing?.id) return;
    try {
      await deleteRetreatPlan(editing.id);
      toast.success("Removed");
      setEditing(null);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const today = todayIso();
  const todayMatchesYear = today.startsWith(`${year}-`);
  const todayMonth = todayMatchesYear ? parseInt(today.slice(5, 7), 10) : 0;
  const todayDay = todayMatchesYear ? parseInt(today.slice(8, 10), 10) : 0;

  // 31 day rows. For months with fewer days, the trailing rows are rendered as "—".
  const dayRows = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <div className="space-y-4" data-testid="annual-calendar-grid">
      {/* Year tabs + legend */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 border border-slate-200 rounded-md p-1 bg-white" data-testid="planner-year-selector">
          {yearsToOffer.map(y => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className={`px-3 py-1.5 rounded text-sm font-medium ${y === year ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid={`planner-year-${y}`}
            >
              {y}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-600 flex-wrap" data-testid="planner-legend">
          {RETREAT_PLAN_STATUSES.map(s => (
            <div key={s.value} className="flex items-center gap-1.5">
              <span className={`h-3 w-5 rounded ${s.colorClass.split(" ")[0]}`} />
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-slate-500">
        Click an empty cell to add a block · click a block to edit · drag a block vertically to move · drag the top or bottom edge to resize
      </p>

      {/* Grid */}
      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto" data-testid="planner-grid">
        <div className="grid" style={{ gridTemplateColumns: `${DAY_COL_W}px repeat(12, minmax(${MIN_MONTH_COL_W}px, 1fr))` }}>
          {/* Header row */}
          <div className="border-b-2 border-slate-300 bg-slate-50/70 sticky left-0 z-20" style={{ height: HEADER_H }} />
          {monthCols.map(mc => (
            <div
              key={`h-${mc.month}`}
              className={`border-b-2 border-slate-300 border-l border-slate-200 flex flex-col justify-center items-center ${mc.operating ? "bg-white" : "bg-slate-100/70"}`}
              style={{ height: HEADER_H }}
              data-testid={`planner-month-header-${mc.month}`}
            >
              <div className={`text-xs uppercase tracking-wider font-semibold ${mc.operating ? "text-slate-800" : "text-slate-400"}`}>
                {MONTH_LABELS[mc.month - 1]}
              </div>
              <div className="text-[10px] text-slate-400">{mc.dim}d</div>
            </div>
          ))}

          {/* Day rows */}
          {dayRows.map(dayIdx => (
            <DayRow
              key={`r-${dayIdx}`}
              dayIdx={dayIdx}
              monthCols={monthCols}
              year={year}
              todayMonth={todayMonth}
              todayDay={todayDay}
              onCellClick={onCellClick}
              onBarMouseDown={onBarMouseDown}
              onBarClick={(p) => {
                // Open edit only if we didn't actually drag.
                if (dragRef.current?.moved) return;
                setEditing({ ...p, mode: "edit" });
              }}
            />
          ))}
        </div>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="planner-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing?.id ? "Edit retreat block" : "New retreat block"}</DialogTitle>
            <DialogDescription className="sr-only">Plan a retreat occupancy block.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Retreat / organizer name</Label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing(v => ({ ...v, name: e.target.value }))}
                  className="mt-1 h-10"
                  placeholder="e.g. Yoga Tribe (interested)"
                  autoFocus
                  data-testid="planner-form-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Start</Label>
                  <Input
                    type="date"
                    value={editing.start_date}
                    onChange={(e) => setEditing(v => ({ ...v, start_date: e.target.value }))}
                    className="mt-1 h-10"
                    data-testid="planner-form-start"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">End</Label>
                  <Input
                    type="date"
                    value={editing.end_date}
                    onChange={(e) => setEditing(v => ({ ...v, end_date: e.target.value }))}
                    className="mt-1 h-10"
                    data-testid="planner-form-end"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1.5">
                  {RETREAT_PLAN_STATUSES.map(s => {
                    const active = editing.status === s.value;
                    return (
                      <button
                        key={s.value}
                        onClick={() => setEditing(v => ({ ...v, status: s.value }))}
                        className={`px-2 py-1.5 rounded-md text-xs font-medium border transition-all ${active ? `${s.colorClass} text-white border-transparent ring-2 ${s.ring} ring-offset-1` : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                        data-testid={`planner-form-status-${s.value}`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Notes <span className="text-slate-300 normal-case tracking-normal">(optional)</span></Label>
                <Textarea
                  value={editing.notes || ""}
                  onChange={(e) => setEditing(v => ({ ...v, notes: e.target.value }))}
                  className="mt-1 min-h-[60px]"
                  data-testid="planner-form-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex sm:justify-between items-center gap-2">
            <div>
              {editing?.id && (
                <Button variant="ghost" onClick={remove} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="planner-form-delete">
                  <Trash2 size={14} /> Delete
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveFromDialog} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="planner-form-save">
                {editing?.id ? "Save" : <><Plus size={14} /> Add</>}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// One row across all 12 months for a given day-of-month index.
// We render the row as a horizontal strip — bars are absolutely positioned
// inside each month cell when they START on this day; once started, the bar
// spans down across following rows on its own (using grid-row span via CSS).
// For simplicity & robustness we instead render a small *bar segment* per row
// for every active retreat, but that would be 31x heavy. Instead, we render
// each retreat-segment ONCE — anchored to its startDay row — and absolutely
// position it to cover the right vertical span.
function DayRow({ dayIdx, monthCols, year, todayMonth, todayDay, onCellClick, onBarMouseDown, onBarClick }) {
  return (
    <>
      {/* Day-number column (sticky left) */}
      <div
        className="border-t border-slate-100 bg-slate-50/70 text-[10px] text-slate-500 tabular-nums flex items-center justify-center sticky left-0 z-10"
        style={{ height: ROW_H }}
      >
        {dayIdx}
      </div>
      {/* Month cells */}
      {monthCols.map(mc => {
        const validDay = dayIdx <= mc.dim;
        const isToday = mc.month === todayMonth && dayIdx === todayDay;
        return (
          <div
            key={`c-${mc.month}-${dayIdx}`}
            className={`relative border-t border-l border-slate-100 ${!validDay ? "bg-slate-100/60" : mc.operating ? "bg-white hover:bg-blue-50/50" : "bg-slate-50/40 hover:bg-blue-50/30"} ${isToday ? "ring-1 ring-blue-400 ring-inset z-[1]" : ""} ${validDay ? "cursor-pointer" : "cursor-not-allowed"}`}
            style={{ height: ROW_H }}
            onClick={(e) => {
              // Ignore clicks that started on a bar (handled by bar's own onClick).
              if (e.target !== e.currentTarget) return;
              if (!validDay) return;
              onCellClick(mc.month, dayIdx);
            }}
            data-testid={`planner-cell-${mc.month}-${dayIdx}`}
          >
            {/* Render any retreat-segment that STARTS on this day in this month */}
            {validDay && mc.segments
              .filter(seg => seg.startDay === dayIdx)
              .map(seg => {
                const trackIdx = mc.placement.get(seg) || 0;
                const tracks = mc.trackCount;
                const widthPct = 100 / tracks;
                const leftPct = trackIdx * widthPct;
                const spanRows = seg.endDay - seg.startDay + 1;
                const height = spanRows * ROW_H - 2; // small gap
                const s = statusByValue(seg.plan.status);
                return (
                  <div
                    key={`bar-${seg.plan.id}-${mc.month}`}
                    style={{
                      position: "absolute",
                      top: 1,
                      left: `calc(${leftPct}% + 2px)`,
                      width: `calc(${widthPct}% - 4px)`,
                      height: `${height}px`,
                      zIndex: 5,
                    }}
                    className={`rounded-md shadow-sm ${s.colorClass} text-white text-[11px] font-medium overflow-hidden border border-black/10 cursor-grab active:cursor-grabbing flex items-start justify-center pt-1 px-1 leading-tight text-center`}
                    onMouseDown={onBarMouseDown("move", seg.plan)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onBarClick(seg.plan);
                    }}
                    title={`${seg.plan.name} · ${fmtDate(seg.plan.start_date)} → ${fmtDate(seg.plan.end_date)}`}
                    data-testid={`planner-bar-${seg.plan.id}-m${mc.month}`}
                  >
                    {/* Top resize handle (only on the segment that contains the actual start) */}
                    {seg.plan.start_date === isoDay(year, mc.month, dayIdx) && (
                      <div
                        className="absolute left-0 right-0 top-0 h-1.5 cursor-ns-resize hover:bg-black/20"
                        onMouseDown={onBarMouseDown("resize-top", seg.plan)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`planner-bar-resize-top-${seg.plan.id}`}
                      />
                    )}
                    <span className="break-words line-clamp-3">{seg.plan.name}</span>
                    {/* Bottom resize handle (only on the segment that contains the actual end) */}
                    {seg.plan.end_date === isoDay(year, mc.month, seg.endDay) && (
                      <div
                        className="absolute left-0 right-0 bottom-0 h-1.5 cursor-ns-resize hover:bg-black/20"
                        onMouseDown={onBarMouseDown("resize-bottom", seg.plan)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`planner-bar-resize-bottom-${seg.plan.id}`}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        );
      })}
    </>
  );
}
